/**
 * Soft-deleted accounts (PRD §5.1, §10.7).
 *
 * Admin-only landing page that lists trashed accounts and lets the Admin
 * restore them. Restoring an account makes it visible again to anyone the
 * §7.2 rules allow; in particular the contacts that fell back to
 * assigned/created visibility while it was trashed get their account-root
 * visibility back.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";

import { BackLink } from "../components/BackLink";

import { ApiError, type TrashedAccount, accountsApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/accounts/trash")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin") {
      throw redirect({ to: "/accounts" });
    }
  },
  component: TrashPage,
});

function TrashPage() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["accounts-trash", { limit: 50, offset: 0 }],
    queryFn: () => accountsApi.listTrash({ limit: 50, offset: 0 }),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <BackLink fallbackTo="/accounts" fallbackLabel="accounts" />
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Trash2 className="size-5 opacity-70" aria-hidden />
          Trash
        </h1>
        <p className="text-sm opacity-60">
          Soft-deleted accounts. Restoring brings the account back along with the visibility cascade
          to its contacts.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Couldn't load trash: {(error as Error).message}
        </div>
      ) : !data || data.items.length === 0 ? (
        <Empty />
      ) : (
        <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
          {data.items.map((a) => (
            <TrashRow key={a.id} account={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Empty() {
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <Trash2 className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium">Trash is empty</p>
      <p className="mt-1 text-sm opacity-60">Deleted accounts will appear here.</p>
    </div>
  );
}

function TrashRow({ account }: { account: TrashedAccount }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const restore = useMutation({
    mutationFn: () => accountsApi.restore(account.id),
    onSuccess: (restored) => {
      queryClient.setQueryData(["account", account.id], restored);
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["accounts-trash"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.reauthRequired) {
        setError("For security, please log out and log back in to confirm this restore.");
        return;
      }
      setError(err instanceof ApiError ? err.message : "Could not restore account.");
    },
  });

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-0.5">
        <div className="truncate text-sm font-medium">{account.name}</div>
        <div className="text-xs opacity-50">
          Deleted {new Date(account.deleted_at).toLocaleString()}
        </div>
        {error && <div className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</div>}
      </div>
      <button
        type="button"
        disabled={restore.isPending}
        onClick={() => {
          setError(null);
          restore.mutate();
        }}
        className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm font-medium hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
      >
        <RotateCcw className="size-4" aria-hidden />
        {restore.isPending ? "Restoring…" : "Restore"}
      </button>
    </li>
  );
}
